/**
 * Config Overrides tab for customizing action configs on inherited modules.
 * Allows brands to customize action configurations for Core and Account modules
 * without modifying the original module definition.
 */
import {
	BarChart,
	BarChart3,
	Bell,
	Box,
	Building,
	Calendar,
	ChartPie,
	ChevronDown,
	ChevronRight,
	CircleDollarSign,
	Cog,
	Database,
	Edit,
	FileText,
	Folder,
	Globe,
	Home,
	Inbox,
	LayoutDashboard,
	LineChart,
	Mail,
	Map as MapIcon,
	Megaphone,
	MessageSquare,
	Monitor,
	PieChart,
	RotateCcw,
	Save,
	Search,
	Settings,
	ShoppingCart,
	Target,
	TrendingUp,
	User,
	Users,
	Zap,
} from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth";

/** Map of icon names to Lucide icon components. */
const iconMap: Record<string, typeof LayoutDashboard> = {
	BarChart,
	BarChart3,
	Bell,
	Box,
	Building,
	Calendar,
	ChartPie,
	CircleDollarSign,
	Cog,
	Database,
	FileText,
	Folder,
	Globe,
	Home,
	Inbox,
	LayoutDashboard,
	LineChart,
	Mail,
	Map: MapIcon,
	Megaphone,
	MessageSquare,
	Monitor,
	PieChart,
	Search,
	Settings,
	ShoppingCart,
	Target,
	TrendingUp,
	User,
	Users,
	Zap,
};

interface ActionOverride {
	moduleActionId: string;
	actionName: string;
	moduleName: string;
	moduleScope: "core" | "account";
	hasOverride: boolean;
	currentConfig: Record<string, unknown>;
	defaultConfig: Record<string, unknown>;
}

interface InheritedModuleWithActions {
	id: string;
	name: string;
	icon: string | null;
	scope: "core" | "account" | "team";
	actions: {
		id: string;
		name: string;
		config: Record<string, unknown> | null;
		actionType: {
			defaultConfig: Record<string, unknown> | null;
		} | null;
	}[];
}

/**
 * Config Overrides management tab
 * Allows teams to customize action configs for inherited Core/Account modules
 */
export function ConfigOverridesTab() {
	const { activeTeam } = useAuthStore();
	const utils = trpc.useUtils();

	// Expanded modules state
	const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

	// Edit dialog state
	const [editingAction, setEditingAction] = useState<ActionOverride | null>(null);
	const [configJson, setConfigJson] = useState("");
	const [jsonError, setJsonError] = useState<string | null>(null);

	// Query for inherited modules (Core and Account) with their actions
	const modulesQuery = trpc.modules.configOverrides.listInheritedModulesWithActions.useQuery();

	// Query for existing overrides
	const overridesQuery = trpc.modules.configOverrides.list.useQuery();

	// Mutations
	const setOverrideMutation = trpc.modules.configOverrides.set.useMutation({
		onSuccess: () => {
			void utils.modules.configOverrides.list.invalidate();
			setEditingAction(null);
			setConfigJson("");
		},
	});

	const deleteOverrideMutation = trpc.modules.configOverrides.delete.useMutation({
		onSuccess: () => {
			void utils.modules.configOverrides.list.invalidate();
		},
	});

	// Get inherited modules (Core and Account) - already filtered by the procedure
	const inheritedModules = modulesQuery.data ?? [];

	// Build a map of overrides by moduleActionId
	const overridesMap = new Map(
		(overridesQuery.data ?? []).map((o) => [o.moduleActionId, o])
	);

	/**
	 * Toggle the expanded state of a module card
	 * @param moduleId - The ID of the module to toggle
	 */
	const toggleModule = (moduleId: string) => {
		setExpandedModules((prev) => {
			const next = new Set(prev);
			if (next.has(moduleId)) {
				next.delete(moduleId);
			}
			else {
				next.add(moduleId);
			}
			return next;
		});
	};

	/**
	 * Open the edit dialog for customizing an action's config
	 * @param action - The action to edit
	 * @param action.id - Action ID
	 * @param action.name - Action name
	 * @param action.config - Current action config
	 * @param action.actionType - Action type with default config
	 * @param module - The parent module
	 */
	const openEditDialog = (
		action: {
			id: string;
			name: string;
			config: Record<string, unknown> | null;
			actionType: {
				defaultConfig: Record<string, unknown> | null;
			} | null;
		},
		module: InheritedModuleWithActions
	) => {
		const override = overridesMap.get(action.id);
		const currentConfig = override?.config ?? action.config ?? {};
		const defaultConfig = action.config ?? action.actionType?.defaultConfig ?? {};

		setEditingAction({
			moduleActionId: action.id,
			actionName: action.name,
			moduleName: module.name,
			moduleScope: module.scope as "core" | "account",
			hasOverride: !!override,
			currentConfig,
			defaultConfig,
		});
		setConfigJson(JSON.stringify(currentConfig, null, 2));
		setJsonError(null);
	};

	/**
	 * Save the current config override
	 */
	const handleSave = () => {
		if (!editingAction) return;

		try {
			const config = JSON.parse(configJson) as Record<string, unknown>;
			setOverrideMutation.mutate({
				moduleActionId: editingAction.moduleActionId,
				config,
			});
		}
		catch {
			setJsonError("Invalid JSON format");
		}
	};

	/**
	 * Reset (delete) a config override to use the module default
	 * @param moduleActionId - The ID of the module action to reset
	 */
	const handleReset = (moduleActionId: string) => {
		deleteOverrideMutation.mutate({ moduleActionId });
	};

	/**
	 * Validate JSON input and update error state
	 * @param value - The JSON string to validate
	 */
	const validateJson = (value: string) => {
		setConfigJson(value);
		try {
			JSON.parse(value);
			setJsonError(null);
		}
		catch {
			setJsonError("Invalid JSON format");
		}
	};

	if (!activeTeam) {
		return (
			<div className="p-4 text-muted-foreground">
				Please select a team to manage config overrides.
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Header */}
			<div>
				<h2 className="text-lg font-semibold">Config Overrides</h2>
				<p className="text-sm text-muted-foreground">
					Customize action configurations for inherited Core and Account modules.
					Changes here only affect {activeTeam.name}.
				</p>
			</div>

			{/* Modules List */}
			<div className="space-y-2">
				{modulesQuery.isLoading ? (
					<div className="p-8 text-center text-muted-foreground">Loading modules...</div>
				) : modulesQuery.error ? (
					<div className="p-8 text-center text-destructive">
						Error: {modulesQuery.error.message}
					</div>
				) : inheritedModules.length === 0 ? (
					<div className="p-8 text-center text-muted-foreground">
						No Core or Account modules available for customization.
					</div>
				) : (
					inheritedModules.map((module) => {
						const isExpanded = expandedModules.has(module.id);
						const moduleActions = module.actions;
						const hasAnyOverrides = moduleActions.some((a) => overridesMap.has(a.id));
						const Icon = iconMap[module.icon ?? ""] ?? LayoutDashboard;

						return (
							<Card key={module.id}>
								<CardHeader
									className="cursor-pointer hover:bg-muted/50 transition-colors"
									onClick={() => {
										toggleModule(module.id);
									}}
								>
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-3">
											{isExpanded ? (
												<ChevronDown className="h-4 w-4" />
											) : (
												<ChevronRight className="h-4 w-4" />
											)}
											<div>
												<CardTitle className="text-base flex items-center gap-2">
													<Icon className="h-4 w-4" />
													{module.name}
												</CardTitle>
												<CardDescription>
													{moduleActions.length} action{moduleActions.length !== 1 ? "s" : ""}
												</CardDescription>
											</div>
										</div>
										<div className="flex items-center gap-2">
											<Badge variant={module.scope === "core" ? "default" : "secondary"}>
												{module.scope === "core" ? "Core" : "Account"}
											</Badge>
											{hasAnyOverrides && (
												<Badge variant="outline">Customized</Badge>
											)}
										</div>
									</div>
								</CardHeader>
								{isExpanded && (
									<CardContent className="pt-0">
										{moduleActions.length === 0 ? (
											<p className="text-sm text-muted-foreground py-2">
												No actions to customize.
											</p>
										) : (
											<div className="space-y-2">
												{moduleActions.map((action) => {
													const hasOverride = overridesMap.has(action.id);
													return (
														<div
															key={action.id}
															className="flex items-center justify-between py-2 px-3 rounded-lg border"
														>
															<div className="flex items-center gap-3">
																<span className="font-medium">{action.name}</span>
																{hasOverride && (
																	<Badge variant="outline" className="text-xs">
																		Customized
																	</Badge>
																)}
															</div>
															<div className="flex items-center gap-2">
																{hasOverride && (
																	<Button
																		variant="ghost"
																		size="sm"
																		onClick={() => {
																			handleReset(action.id);
																		}}
																		disabled={deleteOverrideMutation.isPending}
																	>
																		<RotateCcw className="h-4 w-4 mr-1" />
																		Reset
																	</Button>
																)}
																<Button
																	variant="outline"
																	size="sm"
																	onClick={() => {
																		openEditDialog(action, module);
																	}}
																>
																	<Edit className="h-4 w-4 mr-1" />
																	{hasOverride ? "Edit" : "Customize"}
																</Button>
															</div>
														</div>
													);
												})}
											</div>
										)}
									</CardContent>
								)}
							</Card>
						);
					})
				)}
			</div>

			{/* Edit Config Dialog */}
			<Dialog
				open={!!editingAction}
				onOpenChange={(open) => {
					if (!open) {
						setEditingAction(null);
						setConfigJson("");
						setJsonError(null);
					}
				}}
			>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							Customize Action Config
						</DialogTitle>
						<DialogDescription>
							Customize the configuration for &quot;{editingAction?.actionName}&quot; in {editingAction?.moduleName}.
							This override only affects {activeTeam.name}.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<div className="flex items-center justify-between">
								<Label htmlFor="config">Configuration (JSON)</Label>
								{editingAction?.hasOverride && (
									<Badge variant="outline">Has Override</Badge>
								)}
							</div>
							<Textarea
								id="config"
								value={configJson}
								onChange={(e) => {
									validateJson(e.target.value);
								}}
								className="font-mono text-sm min-h-[200px]"
								placeholder="{}"
							/>
							{jsonError && (
								<p className="text-sm text-destructive">{jsonError}</p>
							)}
						</div>
						{editingAction && (
							<div className="grid gap-2">
								<Label>Default Configuration (read-only)</Label>
								<pre className="p-3 rounded-lg bg-muted text-sm font-mono overflow-auto max-h-[150px]">
									{JSON.stringify(editingAction.defaultConfig, null, 2)}
								</pre>
							</div>
						)}
					</div>
					{setOverrideMutation.error && (
						<p className="text-sm text-destructive mb-4">{setOverrideMutation.error.message}</p>
					)}
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								setEditingAction(null);
								setConfigJson("");
								setJsonError(null);
							}}
						>
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							disabled={!!jsonError || setOverrideMutation.isPending}
						>
							<Save className="h-4 w-4 mr-1" />
							{setOverrideMutation.isPending ? "Saving..." : "Save Override"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
